export type CompanyDocumentSectionKey = 'financial' | 'other' | 'corporate';

export interface CompanyDocumentSectionConfig {
  key: CompanyDocumentSectionKey;
  title: string;
  emptyState: string;
  uploadButtonLabel: string;
  defaultDocumentType: string;
  defaultExpanded: boolean;
}

const CORPORATE_DOCUMENT_TYPES = new Set([
  'INCORPORATION',
  'SHAREHOLDER_REGISTER',
  'BOARD_RESOLUTION',
  'FILING',
  'CERTIFICATE',
  'CORRESPONDENCE',
]);

export const COMPANY_DOCUMENT_SECTIONS: CompanyDocumentSectionConfig[] = [
  {
    key: 'financial',
    title: 'Financial Statements',
    emptyState: 'No financial statements uploaded yet.',
    uploadButtonLabel: 'Upload Financial Statement',
    defaultDocumentType: 'FINANCIAL_STATEMENT',
    defaultExpanded: true,
  },
  {
    key: 'other',
    title: 'Other Documents',
    emptyState: 'No other documents uploaded yet.',
    uploadButtonLabel: 'Upload Other Document',
    defaultDocumentType: 'OTHER',
    defaultExpanded: true,
  },
  {
    key: 'corporate',
    title: 'Corporate & Governance',
    emptyState: 'No corporate or governance documents uploaded yet.',
    uploadButtonLabel: 'Upload Corporate Document',
    defaultDocumentType: 'INCORPORATION',
    defaultExpanded: false,
  },
];

const normalizeDocumentType = (documentType?: string): string => {
  return (documentType || 'OTHER').toUpperCase();
};

export const getCompanyDocumentSection = (documentType?: string): CompanyDocumentSectionKey => {
  const normalizedType = normalizeDocumentType(documentType);

  if (normalizedType === 'FINANCIAL_STATEMENT') {
    return 'financial';
  }

  if (CORPORATE_DOCUMENT_TYPES.has(normalizedType)) {
    return 'corporate';
  }

  return 'other';
};

export const bucketCompanyDocumentsBySection = <T extends { documentType?: string }>(
  documents: T[],
): Record<CompanyDocumentSectionKey, T[]> => {
  const buckets: Record<CompanyDocumentSectionKey, T[]> = {
    financial: [],
    other: [],
    corporate: [],
  };

  documents.forEach((document) => {
    buckets[getCompanyDocumentSection(document.documentType)].push(document);
  });

  return buckets;
};
